import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBoolean, UseBoolean } from "@newHooks/common/useBoolean";

export const enum NoneSideBarRoutePath {
  FileView = "FILE_VIEW",
  EconsentSignView = "ECONSENT_SIGN_VIEW",
  SharingView = "SHARING_VIEW",
}

const noneSideBarRouteList: NoneSideBarRoute[] = [
  { name: NoneSideBarRoutePath.FileView, path: /\/files\/\d+$/ },
  { name: NoneSideBarRoutePath.SharingView, path: /\/sharings\/[^/]+(\/[^/]+)*$/ },
];

type NoneSideBarRoute = {
  name: string;
  path: string | RegExp;
};

export type LayoutContextProps = {
  sidebarCollapsed: UseBoolean;
  onSidebarPinToggle: VoidFunction;
  isHiddenSideBar: UseBoolean;
};

const getSidebarCollapsedState = () => localStorage.getItem("sidebarCollapsed") === "UnCollapsed";

const LayoutContext = createContext<LayoutContextProps>({
  sidebarCollapsed: {
    value: getSidebarCollapsedState(),
    onTrue: () => {},
    onFalse: () => {},
    onToggle: () => {},
    setValue: () => {},
  },
  onSidebarPinToggle: () => {},
  isHiddenSideBar: {
    value: false,
    onTrue: () => {},
    onFalse: () => {},
    onToggle: () => {},
    setValue: () => {},
  },
});

function LayoutContextProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  const defaultSideBarHidden = noneSideBarRouteList.some(({ path }) =>
    typeof path === "string" ? pathname === path : path.test(pathname)
  );

  const sidebarCollapsed = useBoolean(getSidebarCollapsedState());
  const isHiddenSideBar = useBoolean(defaultSideBarHidden);

  useEffect(() => {
    const shouldBeHidden = noneSideBarRouteList.some(({ path }) => (typeof path === "string" ? pathname === path : path.test(pathname)));

    if (isHiddenSideBar.value !== shouldBeHidden) {
      isHiddenSideBar.setValue(shouldBeHidden);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleSidebarPinTrue = () => {
    sidebarCollapsed.onTrue();
    localStorage.setItem("sidebarCollapsed", "Collapsed");
  };

  const handleSidebarPinFalse = () => {
    sidebarCollapsed.onFalse();
    localStorage.setItem("sidebarCollapsed", "UnCollapsed");
  };

  const handleSidebarPinToggle = () => {
    sidebarCollapsed.onToggle();
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed.value ? "Collapsed" : "UnCollapsed");
  };

  return (
    <LayoutContext.Provider
      value={{
        sidebarCollapsed: { ...sidebarCollapsed, onTrue: handleSidebarPinTrue, onFalse: handleSidebarPinFalse },
        onSidebarPinToggle: handleSidebarPinToggle,
        isHiddenSideBar,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

function useLayoutContext() {
  return useContext(LayoutContext);
}

export { LayoutContext, LayoutContextProvider, useLayoutContext };
